export interface ParsedSellerReference {
  id: string;
  name: string | null;
  value: string;
}

const normalizeOptionalText = (value: unknown): string | null => {
  if (typeof value !== 'string' && typeof value !== 'number') return null;
  const normalized = String(value).trim();
  return normalized && normalized.toLowerCase() !== 'null' ? normalized : null;
};

export const parseSellerReference = (value: unknown): ParsedSellerReference | null => {
  const normalized = normalizeOptionalText(value);
  if (!normalized) return null;

  const separatorIndex = normalized.indexOf('§');
  if (separatorIndex === -1) {
    return { id: normalized, name: null, value: normalized };
  }

  const id = normalized.slice(0, separatorIndex).trim();
  const name = normalized.slice(separatorIndex + 1).trim();

  return {
    id: id || normalized,
    name: name || null,
    value: normalized,
  };
};

interface CampaignContextNameInput {
  sellerReference?: unknown;
  sellerName?: unknown;
  teamName?: unknown;
  /**
   * Team's `seller_ids` registry (CSV of `id§Name`). Needed when the campaign
   * carries a BARE seller ID ("71005" — no embedded name, as returned by
   * campaign_metrics' flattened custom field): the display name is resolved
   * from this registry before falling back to the team name.
   */
  registry?: unknown;
}

/** Resolve a bare seller ID to its display name from the `id§Name` CSV registry. */
const findSellerNameInRegistry = (sellerId: string, registry: unknown): string | null => {
  if (typeof registry !== 'string' || !registry.trim()) return null;
  for (const entry of registry.split(',')) {
    const parsed = parseSellerReference(entry);
    if (parsed?.id === sellerId && parsed.name) return parsed.name;
  }
  return null;
};

export const resolveCampaignContextName = ({
  sellerReference,
  sellerName,
  teamName,
  registry,
}: CampaignContextNameInput): string | null => {
  const parsedSeller = parseSellerReference(sellerReference);
  const registryName = parsedSeller && !parsedSeller.name
    ? findSellerNameInRegistry(parsedSeller.id, registry)
    : null;
  return parsedSeller?.name || registryName || normalizeOptionalText(sellerName) || normalizeOptionalText(teamName);
};
